import { useEffect, useState, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { connectSocket } from '../../utils/socket';
import QUIZ_THEMES from '../../utils/quizThemes';
import { Hourglass, Trophy, Check } from 'lucide-react';
import FullscreenButton from '../../components/FullscreenButton';
import useProjectionZoom from '../../utils/useProjectionZoom';
import { sound } from '../../utils/sound';

const OPTION_LABELS = { a: 'A', b: 'B', c: 'C', d: 'D' };
const OPTION_COLORS = {
  a: '#E8F0FE', b: '#FBF5E6', c: '#EAF2EE', d: '#FDECEA',
  correct: '#5B8A75', wrong: '#E5E0D8',
};

export default function AdminQuizLive() {
  const { sessionId } = useParams();
  const { state } = useLocation();
  const zoom = useProjectionZoom();
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const timerRef = useRef(null);
  const soundRef = useRef(state?.soundProjector === true);

  const [bgColor, setBgColor] = useState('dark');
  const [bgImage, setBgImage] = useState(null);
  const [phase, setPhase] = useState('waiting'); // waiting | question | result | finished
  const [question, setQuestion] = useState(null);
  const [questionMeta, setQuestionMeta] = useState(null); // index, total, timeLimitSec
  const [timeLeft, setTimeLeft] = useState(0);
  const [correctOption, setCorrectOption] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [lang, setLang] = useState('de');

  useEffect(() => {
    const socket = connectSocket();
    socketRef.current = socket;

    socket.emit('admin:join_session', { sessionId, mosqueId: state?.mosqueId || 'admin' });

    socket.on('session:state', ({ bgColor: bc, bgImage: bi }) => {
      if (bc) setBgColor(bc);
      if (bi !== undefined) setBgImage(bi);
    });

    socket.on('question:start', ({ question: q, index, total, timeLimitSec }) => {
      setQuestion(q);
      setQuestionMeta({ index, total, timeLimitSec });
      setCorrectOption(null);
      setAnsweredCount(0);
      setPhase('question');
      if (soundRef.current) sound.start();
      setTimeLeft(timeLimitSec);

      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) { clearInterval(timerRef.current); return 0; }
          return t - 1;
        });
      }, 1000);
    });

    socket.on('question:end', ({ correctOption: co, leaderboard: lb }) => {
      setCorrectOption(co);
      setLeaderboard(lb);
      setPhase('result');
      if (soundRef.current) sound.reveal();
      if (timerRef.current) clearInterval(timerRef.current);
    });

    socket.on('answer:result', () => {
      setAnsweredCount(n => n + 1);
    });

    socket.on('lobby:update', ({ participants }) => {
      setTotalParticipants(participants.length);
    });

    socket.on('quiz:finished', ({ leaderboard: lb }) => {
      setLeaderboard(lb);
      setPhase('finished');
      if (timerRef.current) clearInterval(timerRef.current);
    });

    return () => {
      socket.off('session:state');
      socket.off('question:start');
      socket.off('question:end');
      socket.off('answer:result');
      socket.off('lobby:update');
      socket.off('quiz:finished');
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [sessionId, state]);

  const theme = QUIZ_THEMES[bgColor] || QUIZ_THEMES.dark;
  const outerBg = bgImage
    ? { backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }
    : { background: theme.bg };
  const pct = questionMeta ? Math.round((timeLeft / questionMeta.timeLimitSec) * 100) : 0;
  const timerColor = pct > 50 ? theme.accent : pct > 20 ? '#C9A84C' : '#E05C4B';

  const liveText = bgImage ? '#fff' : theme.text;

  return (
    <div style={{ minHeight: '100vh', ...outerBg }}>
      <FullscreenButton />
    <div style={{ minHeight: '100vh', background: bgImage ? 'rgba(0,0,0,0.6)' : 'transparent', color: liveText, padding: 24 }}>
      <div style={{ maxWidth: 900, margin: '0 auto', zoom }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: theme.text }}>Ilmify — Live</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button
              onClick={() => setLang(l => l === 'de' ? 'al' : 'de')}
              style={{ background: theme.surface, border: `1px solid ${theme.border}`, color: theme.text, padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}
            >
              {lang === 'de' ? '🇩🇪 DE' : '🇦🇱 AL'}
            </button>
            <button
              onClick={() => navigate('/admin/quizzes')}
              style={{ background: theme.surface, border: `1px solid ${theme.border}`, color: `${theme.text}88`, padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}
            >
              ✕ Beenden
            </button>
          </div>
        </div>

        {/* Waiting */}
        {phase === 'waiting' && (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
              <Hourglass size={40} color={`${theme.text}88`} strokeWidth={1.5} />
            </div>
            <div style={{ fontSize: 22, color: `${theme.text}88` }}>Warte auf erste Frage...</div>
          </div>
        )}

        {/* Question */}
        {(phase === 'question' || phase === 'result') && question && (
          <div>
            {/* Progress + Timer */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ color: `${theme.text}88`, fontSize: 14 }}>
                Frage {(questionMeta?.index ?? 0) + 1} / {questionMeta?.total}
              </span>
              {phase === 'question' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 200, height: 8, background: theme.surface, borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: timerColor, transition: 'width 1s linear, background 0.3s' }} />
                  </div>
                  <span style={{ fontSize: 24, fontWeight: 700, color: timerColor, minWidth: 32 }}>{timeLeft}</span>
                </div>
              )}
              {phase === 'result' && (
                <span style={{ fontSize: 13, color: `${theme.text}88` }}>
                  Antworten: {answeredCount} / {totalParticipants || '—'}
                </span>
              )}
            </div>

            {/* Question text */}
            <div style={{
              background: theme.surface, border: `1px solid ${theme.border}`,
              borderRadius: 16, padding: '28px 32px', marginBottom: 20,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
              color: theme.text,
            }}>
              {question.image_path && (
                <img
                  src={`/uploads/${question.image_path}`}
                  alt=""
                  style={{ display: 'block', maxWidth: '100%', maxHeight: 260, borderRadius: 10, objectFit: 'contain' }}
                />
              )}
              <span style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.5, textAlign: 'center' }}>
                {lang === 'de' ? question.text_de : (question.text_al || question.text_de)}
              </span>
            </div>

            {/* Options */}
            {question.type === 'multiple_choice' ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {['a', 'b', 'c', 'd'].map(opt => {
                  const text = lang === 'de' ? question[`option_${opt}_de`] : (question[`option_${opt}_al`] || question[`option_${opt}_de`]);
                  const isCorrect = phase === 'result' && correctOption === opt;
                  const isWrong = phase === 'result' && correctOption && correctOption !== opt;
                  return (
                    <div key={opt} style={{
                      background: isCorrect ? '#5B8A75' : theme.surface,
                      border: `2px solid ${isCorrect ? '#5B8A75' : theme.border}`,
                      borderRadius: 12, padding: '16px 20px',
                      display: 'flex', alignItems: 'center', gap: 14,
                      opacity: isWrong ? 0.4 : 1,
                      transition: 'all 0.3s',
                    }}>
                      <span style={{
                        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                        background: isCorrect ? 'rgba(255,255,255,0.2)' : theme.bg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: 16, color: isCorrect ? '#fff' : `${theme.text}88`,
                      }}>
                        {OPTION_LABELS[opt]}
                      </span>
                      <span style={{ fontSize: 16, fontWeight: 500, color: theme.text }}>{text}</span>
                      {isCorrect && <Check size={18} style={{ marginLeft: 'auto' }} />}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {['a', 'b'].map(opt => {
                  const text = lang === 'de' ? question[`option_${opt}_de`] : (question[`option_${opt}_al`] || question[`option_${opt}_de`]);
                  const isCorrect = phase === 'result' && correctOption === opt;
                  return (
                    <div key={opt} style={{
                      background: isCorrect ? '#5B8A75' : theme.surface,
                      border: `2px solid ${isCorrect ? '#5B8A75' : theme.border}`,
                      borderRadius: 12, padding: '24px',
                      textAlign: 'center', fontSize: 20, fontWeight: 700, color: theme.text,
                      opacity: (!isCorrect && phase === 'result') ? 0.4 : 1,
                      transition: 'all 0.3s',
                    }}>
                      {text} {isCorrect && <Check size={16} style={{ display: 'inline', verticalAlign: 'middle', marginLeft: 4 }} />}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Leaderboard after question */}
            {phase === 'result' && leaderboard.length > 0 && (state?.leaderboardProjector !== false) && (
              <div style={{ marginTop: 24, background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 20 }}>
                <div style={{ fontSize: 13, color: `${theme.text}88`, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Zwischenrangliste</div>
                {leaderboard.slice(0, 5).map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: i < 4 ? `1px solid ${theme.border}` : 'none' }}>
                    <span style={{ width: 28, textAlign: 'center', fontWeight: 700, color: i === 0 ? '#C9A84C' : i === 1 ? '#aaa' : i === 2 ? '#C17B5C' : `${theme.text}44` }}>
                      {i + 1}.
                    </span>
                    <span style={{ flex: 1, fontWeight: 600, color: theme.text }}>{p.firstName} {p.lastName}</span>
                    <span style={{ fontWeight: 700, color: theme.accent }}>{p.score} Pkt.</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Finished */}
        {phase === 'finished' && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
                <Trophy size={48} color="#C9A84C" strokeWidth={1.5} />
              </div>
              <h2 style={{ fontSize: 28, color: theme.text }}>Quiz beendet!</h2>
            </div>
            <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 16, overflow: 'hidden' }}>
              {leaderboard.map((p, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 16, padding: '16px 24px',
                  borderBottom: `1px solid ${theme.border}`,
                  background: i === 0 ? 'rgba(201,168,76,0.1)' : 'transparent',
                }}>
                  <span style={{
                    fontSize: i < 3 ? 28 : 18, width: 40, textAlign: 'center',
                    color: i === 0 ? '#C9A84C' : i === 1 ? '#aaa' : i === 2 ? '#C17B5C' : `${theme.text}44`,
                  }}>
                    {i + 1}.
                  </span>
                  <span style={{ flex: 1, fontSize: 18, fontWeight: 600, color: theme.text }}>{p.firstName} {p.lastName}</span>
                  <span style={{ fontSize: 22, fontWeight: 700, color: theme.accent }}>{p.score}</span>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: 32 }}>
              <button
                className="btn btn-primary btn-lg"
                onClick={() => navigate('/admin/quizzes')}
              >
                Zurück zur Übersicht
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
}
